import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  Bars3Icon,
  BookOpenIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  InformationCircleIcon,
  LightBulbIcon,
  PencilSquareIcon,
  PlusIcon,
  ShieldCheckIcon,
  SparklesIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { agentInstanceService } from "../../../services/agentService";
import { Button } from '../../ui/Button';
import { TextField } from '../../ui/Field';
import { Overlay } from '../../ui/Overlay';
import { Surface } from '../../ui/Surface';

interface Props {
  agent: any;
}

type InstructionItem = {
  id: string;
  text: string;
  active: boolean;
};

type InstructionGroup = {
  id: string;
  title: string;
  items: InstructionItem[];
};

type TemplateSection = {
  id: string;
  title: string;
  subtitle: string;
  items: string[];
};

type PendingDelete =
  | { type: "group"; groupId: string; label: string }
  | { type: "item"; groupId: string; itemId: string; label: string }
  | null;

const INITIAL_GROUPS: InstructionGroup[] = [
  {
    id: "greeting",
    title: "Greeting Instructions",
    items: [
      {
        id: "greeting-1",
        text: "Always start with: “Welcome. My name is Aisa, your IT Support Agent, here to help with IT issues and system monitoring. How can I help you today?”",
        active: true,
      },
      {
        id: "greeting-2",
        text: "Use a warm, natural, and confident tone to build trust from the first message.",
        active: true,
      },
    ],
  },
  {
    id: "during-interaction",
    title: "During Interaction",
    items: [],
  },
  {
    id: "things-to-avoid",
    title: "Things to Avoid",
    items: [],
  },
  {
    id: "escalation",
    title: "Escalation Procedures",
    items: [],
  },
];

const SAMPLE_TEMPLATE_SECTIONS: TemplateSection[] = [
  {
    id: "sample-greeting",
    title: "Greeting Instructions",
    subtitle: "Sample instructions for greeting instructions",
    items: [
      "Start with a warm, professional greeting appropriate for the communication channel",
      "Always introduce yourself clearly and ask how you can help",
      "Use a friendly and welcoming tone from the first interaction",
      "Verify the customer's identity if required by company policy",
    ],
  },
  {
    id: "sample-during-interaction",
    title: "During Interaction",
    subtitle: "Sample instructions for during interaction",
    items: [
      "Listen actively without interrupting the customer",
      "Use empathetic language to acknowledge customer concerns",
      "Ask clarifying questions to understand the issue completely",
    ],
  },
];

function normalizeGroupsFromApi(raw: unknown): InstructionGroup[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map((g: any, idx: number) => ({
    id: typeof g?.id === "string" && g.id ? g.id : `group-${idx}-${Date.now()}`,
    title: typeof g?.title === "string" && g.title.trim() ? g.title.trim() : "Untitled",
    items: Array.isArray(g?.items)
      ? g.items.map((it: any, j: number) => ({
          id: typeof it?.id === "string" && it.id ? it.id : `item-${idx}-${j}-${Date.now()}`,
          text: typeof it?.text === "string" ? it.text : "",
          active: it?.active !== false,
        }))
      : [],
  }));
}

function serializeGroups(groups: InstructionGroup[]) {
  return groups.map((g) => ({
    id: g.id,
    title: g.title.trim() || "Untitled",
    items: g.items.map((it) => ({
      id: it.id,
      text: it.text,
      active: it.active,
    })),
  }));
}

function buildAiSettingsPayload(agent: any, instructionGroups: ReturnType<typeof serializeGroups>) {
  // Send only the slice this tab owns. The AI Settings tab writes model / temperature /
  // systemPrompt / llm ... into the same blob; the server shallow-merges `settings`, so
  // echoing those keys here (with defaults, when this tab never loaded them) is exactly
  // what used to wipe the configured provider/model on every instructions save.
  return {
    schemaVersion: Number(agent?.version) || 1,
    settings: {
      instructionGroups,
    },
  };
}

const InstructionsTab: React.FC<Props> = ({ agent }) => {
  const [groups, setGroups] = useState<InstructionGroup[]>(INITIAL_GROUPS);
  const [expandedGroupId, setExpandedGroupId] = useState<string>("greeting");
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [activeTemplateTab, setActiveTemplateTab] = useState<"sample" | "guardrails">("sample");
  const [pendingDelete, setPendingDelete] = useState<PendingDelete>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editingGroupTitle, setEditingGroupTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!agent?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const raw = agent.aiSettings?.settings?.instructionGroups ?? agent.aiSettings?.instructionGroups;
    if (Array.isArray(raw)) {
      const normalized = normalizeGroupsFromApi(raw);
      if (normalized.length) {
        setGroups(normalized);
        setExpandedGroupId((prev) => (normalized.some((g) => g.id === prev) ? prev : normalized[0].id));
      } else {
        setGroups([]);
        setExpandedGroupId("");
      }
    } else {
      setGroups(INITIAL_GROUPS);
      setExpandedGroupId("greeting");
    }
    setLoading(false);
  }, [agent]);

  const stats = useMemo(() => {
    const categories = groups.length;
    const totalInstructions = groups.reduce((sum, group) => sum + group.items.length, 0);
    const activeRules = groups.reduce((sum, group) => sum + group.items.filter((item) => item.active).length, 0);
    return { categories, totalInstructions, activeRules };
  }, [groups]);

  const toggleInstruction = (groupId: string, itemId: string) => {
    setGroups((prev) =>
      prev.map((group) =>
        group.id !== groupId
          ? group
          : {
              ...group,
              items: group.items.map((item) => (item.id === itemId ? { ...item, active: !item.active } : item)),
            }
      )
    );
  };

  const addInstructionRow = (groupId: string) => {
    const newId = `new-${Date.now()}`;
    setGroups((prev) =>
      prev.map((group) =>
        group.id !== groupId
          ? group
          : {
              ...group,
              items: [
                ...group.items,
                {
                  id: newId,
                  text: "",
                  active: true,
                },
              ],
            }
      )
    );
    setExpandedGroupId(groupId);
    setEditingItemId(newId);
  };

  const updateInstructionText = (groupId: string, itemId: string, text: string) => {
    setGroups((prev) =>
      prev.map((group) =>
        group.id !== groupId
          ? group
          : {
              ...group,
              items: group.items.map((item) => (item.id === itemId ? { ...item, text } : item)),
            }
      )
    );
  };

  const addInstructionGroup = () => {
    const id = `group-${Date.now()}`;
    setGroups((prev) => [...prev, { id, title: "New category", items: [] }]);
    setExpandedGroupId(id);
    setEditingGroupId(id);
    setEditingGroupTitle("New category");
  };

  const beginEditGroupTitle = (group: InstructionGroup) => {
    setEditingGroupId(group.id);
    setEditingGroupTitle(group.title);
  };

  const commitGroupTitle = (groupId: string) => {
    const next = editingGroupTitle.trim() || "Untitled";
    setGroups((prev) => prev.map((g) => (g.id === groupId ? { ...g, title: next } : g)));
    setEditingGroupId(null);
  };

  const applyTemplateSection = (section: TemplateSection) => {
    setGroups((prev) => {
      const existing = prev.find((g) => g.title.trim().toLowerCase() === section.title.trim().toLowerCase());
      const newItems: InstructionItem[] = section.items.map((text, i) => ({
        id: `tpl-${Date.now()}-${i}`,
        text,
        active: true,
      }));
      if (existing) {
        return prev.map((g) =>
          g.id !== existing.id
            ? g
            : {
                ...g,
                items: [...g.items, ...newItems],
              }
        );
      }
      const id = `group-${Date.now()}`;
      return [
        ...prev,
        {
          id,
          title: section.title,
          items: newItems,
        },
      ];
    });
    toast.success(`Added template: ${section.title}`);
  };

  const requestDeleteGroup = (groupId: string, label: string) => {
    setPendingDelete({ type: "group", groupId, label });
  };

  const requestDeleteItem = (groupId: string, itemId: string, label: string) => {
    setPendingDelete({ type: "item", groupId, itemId, label });
  };

  const handleConfirmDelete = () => {
    if (!pendingDelete) return;

    if (pendingDelete.type === "group") {
      setGroups((prev) => prev.filter((group) => group.id !== pendingDelete.groupId));
      if (expandedGroupId === pendingDelete.groupId) {
        setExpandedGroupId("");
      }
    } else {
      setGroups((prev) =>
        prev.map((group) =>
          group.id !== pendingDelete.groupId
            ? group
            : {
                ...group,
                items: group.items.filter((item) => item.id !== pendingDelete.itemId),
              }
        )
      );
    }

    setPendingDelete(null);
  };

  const handleSave = async () => {
    if (!agent?.id) {
      toast.error("Agent id missing. Cannot save instructions.");
      return;
    }
    setSaving(true);
    try {
      const payload = buildAiSettingsPayload(agent, serializeGroups(groups));
      await agentInstanceService.updateAgentInstanceAiSettings(agent.id, payload);
      toast.success("Instructions saved.");
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message ?? "Failed to save instructions.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-agentic-text-secondary">Loading instructions…</div>;
  }

  return (
    <div className="p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-2xl font-semibold leading-tight text-c-1f1f1f">Agent Instructions</h3>
          <p className="mt-2 text-base font-normal text-c-7b8796">
            Configure how your agent should behave across all communication channels. Active instructions are merged into
            chat prompts with your AI system prompt.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button tone="custom"
            type="button"
            onClick={addInstructionGroup}
            className="inline-flex h-11 items-center gap-2 rounded-full border border-c-c3ccd8 bg-white px-5 text-base font-medium text-c-1f1f1f"
          >
            <PlusIcon className="h-4 w-4" />
            Add category
          </Button>
          <Button tone="custom"
            type="button"
            onClick={() => setIsTemplateModalOpen(true)}
            className="inline-flex h-11 items-center gap-2 rounded-full bg-c-ebedf1 px-5 text-base font-medium text-c-1f1f1f"
          >
            <SparklesIcon className="h-4 w-4" />
            Add Instructions
          </Button>
        </div>
      </div>

      <div className="mt-7 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-c-cfe1ff bg-c-f7fbff px-5 py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-c-7e8da0">Categories</p>
              <p className="mt-1 text-4xl font-semibold text-c-1f1f1f">{stats.categories}</p>
            </div>
            <div className="rounded-xl bg-c-e6effb p-3 text-c-2e6ecf">
              <BookOpenIcon className="h-6 w-6" />
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-[#caebd3] bg-c-f6fff8 px-5 py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-c-7e8da0">Total Instructions</p>
              <p className="mt-1 text-4xl font-semibold text-c-1f1f1f">{stats.totalInstructions}</p>
            </div>
            <div className="rounded-xl bg-c-e8f5ec p-3 text-[#1f8f44]">
              <CheckIcon className="h-6 w-6" />
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-c-e2d9f8 bg-c-faf8ff px-5 py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-c-7e8da0">Active Rules</p>
              <p className="mt-1 text-4xl font-semibold text-c-1f1f1f">{stats.activeRules}</p>
            </div>
            <div className="rounded-xl bg-c-eee8fb p-3 text-c-7352d3">
              <ShieldCheckIcon className="h-6 w-6" />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 space-y-4">
        {groups.length === 0 ? (
          <p className="rounded-xl border border-dashed border-c-d7dde7 bg-c-fbfcfe px-5 py-8 text-center text-c-7b8796">
            No instruction categories yet. Add a category or use a template.
          </p>
        ) : (
          groups.map((group) => {
            const isExpanded = expandedGroupId === group.id;
            return (
              <Surface as="section" key={group.id} className="overflow-hidden rounded-xl border border-c-d7dde7 bg-white">
                <div className="flex w-full items-center justify-between px-5 py-4 text-left">
                  <div className="flex min-w-0 flex-1 items-center gap-4">
                    <div className="rounded-xl bg-c-edf3fb p-2 text-c-2f6dcb">
                      <LightBulbIcon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      {editingGroupId === group.id ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <TextField
                            type="text"
                            value={editingGroupTitle}
                            onChange={(e) => setEditingGroupTitle(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") commitGroupTitle(group.id);
                              if (e.key === "Escape") setEditingGroupId(null);
                            }}
                            className="min-w-[12rem] flex-1 rounded-[12px] border border-c-1f1f1f bg-white px-3 py-2 text-[1rem] text-c-1f1f1f outline-none"
                          />
                          <Button tone="custom"
                            type="button"
                            onClick={() => commitGroupTitle(group.id)}
                            className="rounded-lg bg-[#2f9444] px-3 py-1.5 text-sm font-medium text-white"
                          >
                            Done
                          </Button>
                        </div>
                      ) : (
                        <Button tone="custom"
                          type="button"
                          className="w-full text-left"
                          onClick={() => beginEditGroupTitle(group)}
                          title="Edit category name"
                        >
                          <p className="truncate text-[1rem] font-semibold leading-tight text-c-2b2f36">{group.title}</p>
                          <p className="mt-1 truncate text-[0.9rem] font-normal text-c-7b8796">
                            {group.items.length} instruction{group.items.length !== 1 ? "s" : ""}
                          </p>
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="ml-4 flex items-center gap-2 shrink-0">
                    <Button tone="custom"
                      type="button"
                      onClick={() => beginEditGroupTitle(group)}
                      className="rounded-xl p-2 text-c-1f1f1f hover:bg-c-f0f2f5"
                      aria-label="Rename category"
                    >
                      <PencilSquareIcon className="h-5 w-5" />
                    </Button>
                    <Button tone="custom"
                      type="button"
                      onClick={() => requestDeleteGroup(group.id, group.title)}
                      className="rounded-xl bg-[#d92e2e] p-2 text-white"
                      aria-label="Delete category"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </Button>
                    <Button tone="custom"
                      type="button"
                      aria-label={isExpanded ? "Collapse section" : "Expand section"}
                      onClick={() => setExpandedGroupId((prev) => (prev === group.id ? "" : group.id))}
                      className="p-1"
                    >
                      {isExpanded ? (
                        <ChevronDownIcon className="h-5 w-5 text-c-1f1f1f" />
                      ) : (
                        <ChevronRightIcon className="h-5 w-5 text-c-1f1f1f" />
                      )}
                    </Button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t border-c-d7dde7 px-5 py-4">
                    <div className="mb-4 flex justify-end">
                      <Button tone="custom"
                        type="button"
                        onClick={() => addInstructionRow(group.id)}
                        className="inline-flex items-center gap-2 text-lg font-medium text-c-1f1f1f"
                      >
                        <PlusIcon className="h-4 w-4" />
                        Add Instruction
                      </Button>
                    </div>

                    <div className="space-y-3">
                      {group.items.length === 0 ? (
                        <p className="text-sm text-c-7b8796">No instructions in this category yet.</p>
                      ) : (
                        group.items.map((item) => (
                          <div
                            key={item.id}
                            className="flex items-center gap-4 rounded-lg border border-c-d7dde7 bg-c-fbfcfe px-4 py-4"
                          >
                            <Bars3Icon className="h-5 w-5 shrink-0 text-c-8e98a8" />
                            <div className="flex-1">
                              {editingItemId === item.id ? (
                                <TextField
                                  type="text"
                                  value={item.text}
                                  onChange={(e) => updateInstructionText(group.id, item.id, e.target.value)}
                                  onBlur={() => setEditingItemId(null)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") setEditingItemId(null);
                                  }}
                                  className="w-full rounded-[12px] border border-c-1f1f1f bg-white px-4 py-3 text-[0.9rem] text-c-1f1f1f outline-none"
                                  placeholder="Instruction text"
                                  autoFocus
                                />
                              ) : (
                                <p className="text-[0.9rem] font-normal text-c-2b2f36">
                                  {item.text.trim() ? item.text : <span className="text-c-7b8796">Empty — click edit</span>}
                                </p>
                              )}
                            </div>
                            <Button tone="custom"
                              type="button"
                              aria-label="Toggle instruction"
                              aria-pressed={item.active}
                              onClick={() => toggleInstruction(group.id, item.id)}
                              className={`relative h-6 w-10 shrink-0 rounded-full transition-colors focus:outline-none ${item.active ? "bg-[#2f9444]" : "bg-c-c6ccd7"}`}
                            >
                              <span
                                className={`absolute top-1 h-4 w-4 rounded-full bg-white transition-transform ${item.active ? "translate-x-5" : "translate-x-1"}`}
                              />
                            </Button>
                            <Button tone="custom"
                              type="button"
                              className="shrink-0 text-c-1f1f1f"
                              aria-label="Edit instruction"
                              onClick={() => setEditingItemId(item.id)}
                            >
                              <PencilSquareIcon className="h-4 w-4" />
                            </Button>
                            <Button tone="custom"
                              type="button"
                              onClick={() => requestDeleteItem(group.id, item.id, item.text || "this instruction")}
                              className="shrink-0 text-[#d92e2e]"
                              aria-label="Delete instruction"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </Surface>
            );
          })
        )}
      </div>

      <div className="mt-6 flex justify-end">
        <Button tone="primary-fade"
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="rounded-lg px-6 py-2 transition-colors disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save Settings"}
        </Button>
      </div>

      {isTemplateModalOpen && (
        <Overlay tone="dim-30" className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="h-[90vh] w-full max-w-[1320px] overflow-hidden rounded-2xl bg-white shadow-xl">
            <div className="flex h-full flex-col">
              <div className="flex items-center justify-between px-6 pt-6">
                <h2 className="text-[2.5rem] font-medium text-c-1f1f1f">Instruction Templates &amp; Best Practices</h2>
                <Button tone="custom" type="button" onClick={() => setIsTemplateModalOpen(false)} className="text-c-4f5665">
                  <XMarkIcon className="h-8 w-8" />
                </Button>
              </div>

              <div className="mt-4 border-b border-c-d8dee8 px-6">
                <div className="flex items-center gap-8">
                  <Button tone="custom"
                    type="button"
                    onClick={() => setActiveTemplateTab("sample")}
                    className={`inline-flex items-center gap-3 border-b-[3px] pb-3 text-[1.1rem] ${
                      activeTemplateTab === "sample"
                        ? "border-c-1f1f1f font-medium text-c-1f1f1f"
                        : "border-transparent text-c-4f5665"
                    }`}
                  >
                    <SparklesIcon className="h-5 w-5" />
                    Sample Instructions
                  </Button>
                  <Button tone="custom"
                    type="button"
                    onClick={() => setActiveTemplateTab("guardrails")}
                    className={`inline-flex items-center gap-3 border-b-[3px] pb-3 text-[1.1rem] ${
                      activeTemplateTab === "guardrails"
                        ? "border-c-1f1f1f font-medium text-c-1f1f1f"
                        : "border-transparent text-c-4f5665"
                    }`}
                  >
                    <ShieldCheckIcon className="h-5 w-5" />
                    Guardrails
                  </Button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-5">
                {activeTemplateTab === "sample" && (
                  <>
                    <div className="mb-5 flex items-start gap-4 rounded-xl bg-c-e8eef8 px-6 py-5">
                      <InformationCircleIcon className="mt-0.5 h-6 w-6 shrink-0 text-c-2f6dcb" />
                      <p className="text-[1rem] text-c-1f1f1f">
                        Browse sample instructions for common scenarios. You can use these as templates for your own
                        categories.
                      </p>
                    </div>

                    <div className="space-y-5">
                      {SAMPLE_TEMPLATE_SECTIONS.map((section) => (
                        <Surface as="section" key={section.id} className="rounded-xl border border-c-d7dde7 bg-white p-5">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <h3 className="text-[1.25rem] font-semibold text-c-2b2f36">{section.title}</h3>
                              <p className="mt-1 text-[0.9rem] text-c-7b8796">{section.subtitle}</p>
                            </div>
                            <Button tone="custom"
                              type="button"
                              onClick={() => applyTemplateSection(section)}
                              className="inline-flex items-center gap-2 rounded-full bg-c-ebedf1 px-5 py-2 text-[1rem] font-medium text-c-1f1f1f"
                            >
                              <PlusIcon className="h-5 w-5" />
                              Use These
                            </Button>
                          </div>
                          <div className="mt-4 border-t border-c-d7dde7 pt-4">
                            <ol className="space-y-3">
                              {section.items.map((item, index) => (
                                <li key={`${section.id}-${index}`} className="flex gap-4 text-[1rem] text-c-2b2f36">
                                  <span className="w-5 text-c-7b8796">{index + 1}.</span>
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ol>
                          </div>
                        </Surface>
                      ))}
                    </div>
                  </>
                )}
                {activeTemplateTab === "guardrails" && (
                  <p className="text-c-5f6775">
                    Define safety and compliance rules in your categories, or extend templates here in a future update.
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between border-t border-c-d8dee8 px-6 py-5">
                <Button tone="custom"
                  type="button"
                  onClick={() => {
                    addInstructionGroup();
                    setIsTemplateModalOpen(false);
                  }}
                  className="inline-flex items-center gap-2 rounded-full bg-c-ebedf1 px-6 py-2 text-[1rem] font-medium text-c-1f1f1f"
                >
                  <PlusIcon className="h-6 w-6" />
                  Add Custom
                </Button>
                <Button tone="custom"
                  type="button"
                  onClick={() => setIsTemplateModalOpen(false)}
                  className="rounded-full border border-c-c3ccd8 px-8 py-2 text-[1rem] font-medium text-c-1f1f1f"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </Overlay>
      )}

      {pendingDelete && (
        <Overlay tone="dim-30" className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-c-1f1f1f">Delete Confirmation</h3>
            <p className="mt-2 text-sm text-c-5f6775">
              Are you sure you want to delete{" "}
              <span className="font-medium text-c-1f1f1f">{pendingDelete.label}</span>? This action cannot be undone.
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <Button tone="custom"
                type="button"
                onClick={() => setPendingDelete(null)}
                className="rounded-lg border border-c-d1d7e2 px-4 py-2 text-sm font-medium text-c-1f1f1f"
              >
                Cancel
              </Button>
              <Button tone="custom"
                type="button"
                onClick={handleConfirmDelete}
                className="rounded-lg bg-[#d92e2e] px-4 py-2 text-sm font-medium text-white"
              >
                Delete
              </Button>
            </div>
          </div>
        </Overlay>
      )}
    </div>
  );
};

export default InstructionsTab;
